import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useUpdateExpense } from "../../hooks/useExpenceApi";
import { toast } from "react-toastify";
import { X } from "lucide-react";
import Button from "../ui/Button";
import api from "../../lib/api";
import { useAuth } from "../../hooks/AuthContext";

const epochToDate = (epoch) =>
  epoch ? new Date(epoch * 1000).toISOString().split("T")[0] : "";

const dateToEpoch = (dateStr) => Math.floor(new Date(dateStr).getTime() / 1000);

export const EditExpenceModal = ({ expense, editingExpense,employees: initialEmployees = [], loadingEmployees: initialLoading = true, onClose }) => {
  const [employees, setEmployees] = useState(initialEmployees);
  const [loading, setLoading] = useState(initialLoading);
  const { register, handleSubmit, reset } = useForm();
  const { mutate: updateExpense, isLoading } = useUpdateExpense();

  const { user } = useAuth();

  // Fetch employees only if not passed
  useEffect(() => {
    if (employees.length === 0) {
      const fetchEmployees = async () => {
        try {
          setLoading(true);
          const res = await api.get("/employees");
          setEmployees(res.data.data.employees);
        } catch (err) {
          console.error("Error fetching employees:", err);
          toast.error("Failed to fetch employees.");
        } finally {
          setLoading(false);
        }
      };
      fetchEmployees();
    } else {
      setLoading(false);
    }
  }, [employees]);



  // Reset form when expense or employees are loaded
  useEffect(() => {
    if (expense) {
      const selectedEmployee = employees.find(e => e._id === expense.employeeId);
      reset({
        employee: selectedEmployee?._id || "",
        description: expense.description || "",
        storeName: expense.shopName || "",
        amount: expense.amount || "",
        status: expense.status || "approve",
        date: epochToDate(expense.date),
      });
    }
  }, [expense, employees, reset]);

  const onSubmit = (formData) => {
    const updatedData = {
      employeeId: formData.employee,
      employeeName: employees.find(e => e._id === formData.employee)?.name || "Unknown",
      description: formData.description,
      shopName: formData.storeName,
      amount: Number(formData.amount),
      status: formData.status || "pending",
      date: dateToEpoch(formData.date),
      updatedBy: user?.employeeProfile?._id
    };

    updateExpense(
      { expenseId: expense._id, data: updatedData },
      {
        onSuccess: () => {
          toast.success("Expense updated successfully!");
          onClose();
        },
        onError: (err) => {
          toast.error(err.response?.data?.message || err.message);
        },
      }
    );
  };

  if (!expense) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-40 flex justify-center items-center">
      <div className="bg-white rounded-lg shadow-xl p-4 h-fit w-[500px] z-50">
        <div className="flex justify-between mb-3">
          <h2 className="text-xl font-bold">Edit Expense</h2>
          <button onClick={onClose} className="bg-gray-200 p-1 rounded-full hover:bg-gray-300">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          {/* Employee */}
          <div>
            <label className="text-gray-700 mb-1 block">Employee</label>

            {user?.role === "employee" ? (
              // ✅ If logged-in user is employee, show their name only
              <p className="p-2 border rounded bg-gray-100 text-gray-700">
                {expense?.employeeName || user?.name || "N/A"}
              </p>
            ) : loading ? (
              // ✅ If loading employees list
              <p>Loading..</p>
            ) : (
              // ✅ Otherwise, show dropdown for HR/Admin
              <select
                {...register("employee")}
                className="w-full p-2 border rounded"
                required
              >
                <option value="">Select Employee</option>
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.name} ({emp.employeeId})
                  </option>
                ))}
              </select>
            )}
          </div>


          {/* Description */}
          <div>
            <label className="text-gray-700 mb-1 block">Description</label>
            <textarea
              {...register("description")}
              className="w-full p-2 border rounded h-16"
              placeholder="Enter description"
              required
            />
          </div>

          {/* Store Name + Amount */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-700 mb-1 block">Store Name</label>
              <input
                {...register("storeName")}
                type="text"
                className="w-full p-2 border rounded"
                placeholder="Enter store name"
                required
              />
            </div>
            <div>
              <label className="text-gray-700 mb-1 block">Amount</label>
              <input
                {...register("amount")}
                type="number"
                className="w-full p-2 border rounded"
                min={0}
                required
              />
            </div>
          </div>

          {/* Status + Date */}
          <div className="grid grid-cols-2 gap-3">
          {user?.role !== "employee" && (
    <div>
      <label className="text-gray-700 mb-1 block">Status</label>
      <select {...register("status")} className="w-full p-2 border rounded">
        <option value="">Select</option>
        <option value="approve">Approve</option>
        <option value="reject">Rejected</option>
      </select>
    </div>
  )}
            <div>
              <label className="text-gray-700 mb-1 block">Date</label>
              <input
                {...register("date")}
                type="date"
                className="w-full p-2 border rounded"
                required
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-3 border-t">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
